import React, { useState } from "react";
import type { VisualizationResponse } from "../types";
import { BotIcon } from "./icons/BotIcon";
import { LoadingSpinner } from "./icons/LoadingSpinner";
import { CopyIcon } from "./icons/CopyIcon";
import { CheckIcon } from "./icons/CheckIcon";
import { ChartRenderer } from "./ChartRenderer";

interface ResultDisplayProps {
  isLoading: boolean;
  error: string | null;
  result: VisualizationResponse | null;
}

const InitialState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full text-center text-gray-600">
    <BotIcon className="w-16 h-16 mb-4 text-blue-600" />
    <h2 className="text-xl font-semibold text-slate-800">Welcome!</h2>
    <p>How can I help you visualize the survey data today?</p>
    <p className="mt-2 text-sm">
      Enter a query or use an example to get started.
    </p>
  </div>
);

const SqlDisplay: React.FC<{ sql: string }> = ({ sql }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative mt-4 bg-slate-900/70 p-4 rounded-lg border border-slate-700">
      <h3 className="text-sm font-semibold mb-2 text-slate-400">
        Generated SQL Query
      </h3>
      <pre className="text-sm text-cyan-300 whitespace-pre-wrap overflow-x-auto font-mono">
        <code>{sql}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors duration-200"
      >
        {copied ? (
          <CheckIcon className="w-4 h-4 text-green-400" />
        ) : (
          <CopyIcon className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};

export const ResultDisplay: React.FC<ResultDisplayProps> = ({
  isLoading,
  error,
  result,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <LoadingSpinner />
          <p className="mt-4 text-slate-400">Generating insights...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center h-full text-center text-red-400">
          <p>{error}</p>
        </div>
      );
    }
    if (result) {
      return (
        <div className="flex flex-col gap-6 animate-fade-in">
          <div>
            <h2 className="text-2xl font-bold text-black">{result.title}</h2>
            <p className="capitalize text-slate-400">
              {result.chart_type} Chart
            </p>
          </div>
          <div className="h-96 w-full">
            <ChartRenderer
              chartType={result.chart_type}
              xAxisLabel={result.x_axis}
              yAxisLabel={result.y_axis}
            />
          </div>
          {/* <SqlDisplay sql={result.sql_query} /> */}
        </div>
      );
    }
    return <InitialState />;
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm min-h-[500px] flex flex-col justify-center">
      {renderContent()}
    </div>
  );
};
